import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const TRANCHE_TYPES = ['Senior Debt', 'Junior Debt', 'Mezzanine', 'Preferred Equity', 'Common Equity', 'Grant', 'Subordinated Debt'];
const RECOURSE_OPTIONS = ['Full', 'Limited', 'Non-recourse'];

const TRANCHE_COLORS = {
  'Senior Debt': '#1e40af',
  'Junior Debt': '#4338ca',
  'Mezzanine': '#6d28d9',
  'Preferred Equity': '#0d9488',
  'Common Equity': '#3b4c4c',
  'Grant': '#059669',
  'Subordinated Debt': '#7c3aed',
};

const BENCHMARK_DATA = [
  { name: 'This Deal', seniorDebt: 60, mezzanine: 0, preferredEquity: 0, commonEquity: 40 },
  { name: 'Sector Avg', seniorDebt: 55, mezzanine: 10, preferredEquity: 5, commonEquity: 30 },
  { name: 'Best in Class', seniorDebt: 65, mezzanine: 5, preferredEquity: 0, commonEquity: 30 },
];

export default function CapitalStructureTab({ deal, readOnly }) {
  const [totalSize, setTotalSize] = useState(deal?.totalDealSize || 120);
  const [ebitda, setEbitda] = useState(18.5);
  const [tranches, setTranches] = useState([
    { id: 1, name: 'Senior Debt', type: 'Senior Debt', amount: deal?.debtTranche || 72, provider: 'NatWest Infrastructure Finance', rate: 4.5, ranking: 1, recourse: 'Full' },
    { id: 2, name: 'Preferred Equity', type: 'Preferred Equity', amount: Math.round((deal?.equityTranche || 48) * 0.4), provider: 'Meridian Capital Partners', rate: 9.0, ranking: 2, recourse: 'Limited' },
    { id: 3, name: 'Common Equity', type: 'Common Equity', amount: Math.round((deal?.equityTranche || 48) * 0.6), provider: 'SeedDrive Fund III', rate: 14.2, ranking: 3, recourse: 'Non-recourse' },
  ]);

  const totalTranches = tranches?.reduce((s, t) => s + (parseFloat(t?.amount) || 0), 0);
  const totalPct = totalSize > 0 ? Math.round((totalTranches / totalSize) * 100) : 0;
  const isBalanced = Math.abs(totalPct - 100) < 1;

  const totalDebt = tranches?.filter((t) => ['Senior Debt', 'Junior Debt', 'Mezzanine', 'Subordinated Debt']?.includes(t?.type))?.reduce((s, t) => s + (parseFloat(t?.amount) || 0), 0);
  const annualDebtService = totalDebt * 0.065;
  const dscr = ebitda > 0 && annualDebtService > 0 ? (ebitda / annualDebtService)?.toFixed(2) : 'N/A';
  const ltv = totalSize > 0 ? ((totalDebt / totalSize) * 100)?.toFixed(1) : 'N/A';

  const addTranche = () => {
    if (readOnly) return;
    setTranches((prev) => [
      ...prev,
      { id: Date.now(), name: 'New Tranche', type: 'Senior Debt', amount: 0, provider: '', rate: 0, ranking: prev?.length + 1, recourse: 'Full' },
    ]);
  };

  const removeTranche = (id) => {
    if (readOnly) return;
    setTranches((prev) => prev?.filter((t) => t?.id !== id));
  };

  const updateTranche = (id, field, value) => {
    if (readOnly) return;
    setTranches((prev) => prev?.map((t) => (t?.id === id ? { ...t, [field]: value } : t)));
  };

  const stackedTranches = [...tranches]?.sort((a, b) => b?.ranking - a?.ranking);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Capital Stack Diagram */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Layers" size={16} color="var(--color-primary)" />
            Capital Stack
          </h3>
          <div className="flex gap-4 items-end">
            <div className="flex flex-col-reverse w-24 rounded-lg overflow-hidden border border-border" style={{ height: 280 }}>
              {stackedTranches?.map((t) => {
                const pct = totalTranches > 0 ? ((parseFloat(t?.amount) || 0) / totalTranches) * 100 : 0;
                const color = TRANCHE_COLORS?.[t?.type] || '#94a3b8';
                return (
                  <div
                    key={t?.id}
                    style={{ height: `${pct}%`, backgroundColor: color, minHeight: pct > 0 ? 20 : 0 }}
                    className="flex items-center justify-center transition-all duration-300"
                    title={`${t?.name}: £${t?.amount}m (${pct?.toFixed(0)}%)`}
                  />
                );
              })}
            </div>
            <div className="flex-1 space-y-2">
              {stackedTranches?.map((t) => {
                const pct = totalTranches > 0 ? ((parseFloat(t?.amount) || 0) / totalTranches) * 100 : 0;
                const color = TRANCHE_COLORS?.[t?.type] || '#94a3b8';
                return (
                  <div key={t?.id} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{t?.name}</p>
                      <p className="text-muted-foreground">£{t?.amount}m · {pct?.toFixed(0)}% · {t?.rate}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DSCR / LTV */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">DSCR</p>
              <p className="text-lg font-bold text-foreground">{dscr}x</p>
              <p className="text-xs text-muted-foreground">Debt Service Coverage</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">LTV</p>
              <p className="text-lg font-bold text-foreground">{ltv}%</p>
              <p className="text-xs text-muted-foreground">Loan to Value</p>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-muted-foreground">Projected EBITDA (£m)</label>
            <input
              type="number"
              value={ebitda}
              onChange={(e) => setEbitda(parseFloat(e?.target?.value) || 0)}
              disabled={readOnly}
              className="mt-1 w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            />
          </div>
        </div>

        {/* Tranche Editor */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Edit3" size={16} color="var(--color-primary)" />
              Tranche Configuration
            </h3>
            <div className="flex items-center gap-2">
              {isBalanced ? (
                <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                  <Icon name="CheckCircle" size={14} color="#059669" /> 100% allocated
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                  <Icon name="AlertCircle" size={14} color="#dc2626" /> {totalPct}% allocated
                </span>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label className="text-xs text-muted-foreground">Total Deal Size (£m)</label>
            <input
              type="number"
              value={totalSize}
              onChange={(e) => setTotalSize(parseFloat(e?.target?.value) || 0)}
              disabled={readOnly}
              className="mt-1 w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            />
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {tranches?.map((t) => {
              const pct = totalSize > 0 ? ((parseFloat(t?.amount) || 0) / totalSize * 100)?.toFixed(1) : '0.0';
              return (
                <div key={t?.id} className="border border-border rounded-lg p-3 bg-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <input
                      value={t?.name}
                      onChange={(e) => updateTranche(t?.id, 'name', e?.target?.value)}
                      disabled={readOnly}
                      className="text-sm font-medium text-foreground bg-transparent border-none outline-none flex-1 disabled:opacity-60"
                    />
                    {!readOnly && (
                      <button onClick={() => removeTranche(t?.id)} className="text-red-400 hover:text-red-600 ml-2">
                        <Icon name="X" size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <label className="text-muted-foreground">Type</label>
                      <select
                        value={t?.type}
                        onChange={(e) => updateTranche(t?.id, 'type', e?.target?.value)}
                        disabled={readOnly}
                        className="w-full mt-0.5 px-2 py-1 border border-border rounded bg-card text-foreground text-xs focus:outline-none disabled:opacity-60"
                      >
                        {TRANCHE_TYPES?.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-muted-foreground">Amount (£m)</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={t?.amount}
                          onChange={(e) => updateTranche(t?.id, 'amount', parseFloat(e?.target?.value) || 0)}
                          disabled={readOnly}
                          className="w-full mt-0.5 px-2 py-1 border border-border rounded bg-card text-foreground text-xs focus:outline-none disabled:opacity-60"
                        />
                        <span className="text-muted-foreground mt-0.5 whitespace-nowrap">{pct}%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-muted-foreground">Rate / Return (%)</label>
                      <input
                        type="number"
                        value={t?.rate}
                        onChange={(e) => updateTranche(t?.id, 'rate', parseFloat(e?.target?.value) || 0)}
                        disabled={readOnly}
                        className="w-full mt-0.5 px-2 py-1 border border-border rounded bg-card text-foreground text-xs focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground">Recourse</label>
                      <select
                        value={t?.recourse}
                        onChange={(e) => updateTranche(t?.id, 'recourse', e?.target?.value)}
                        disabled={readOnly}
                        className="w-full mt-0.5 px-2 py-1 border border-border rounded bg-card text-foreground text-xs focus:outline-none disabled:opacity-60"
                      >
                        {RECOURSE_OPTIONS?.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-muted-foreground">Provider / Investor</label>
                      <input
                        value={t?.provider}
                        onChange={(e) => updateTranche(t?.id, 'provider', e?.target?.value)}
                        disabled={readOnly}
                        className="w-full mt-0.5 px-2 py-1 border border-border rounded bg-card text-foreground text-xs focus:outline-none disabled:opacity-60"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <button
              onClick={addTranche}
              className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              <Icon name="Plus" size={14} /> Add Tranche
            </button>
          )}
        </div>
      </div>
      {/* Benchmark Chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={16} color="var(--color-primary)" />
          Capital Structure vs Sector Benchmarks
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={BENCHMARK_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} unit="%" />
            <Tooltip formatter={(v) => `${v}%`} />
            <Legend />
            <Bar dataKey="seniorDebt" name="Senior Debt" stackId="a" fill="#1e40af" />
            <Bar dataKey="mezzanine" name="Mezzanine" stackId="a" fill="#6d28d9" />
            <Bar dataKey="preferredEquity" name="Preferred Equity" stackId="a" fill="#0d9488" />
            <Bar dataKey="commonEquity" name="Common Equity" stackId="a" fill="#3b4c4c" />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs text-muted-foreground mt-2 italic">Sample benchmark data for demonstration purposes only</p>
      </div>
    </div>
  );
}
